// Libs
import clsx from "clsx";
import React from "react";

// Utils
import { wrapKeyUp, wrapLabel, wrapPrevent } from "../../core/utils";

// Components
import {
    type IconPosition,
    type IconSize,
    IconWrapper,
    type IconWrapperProps,
    type Icons,
} from "../icons";
import { LoaderIcon } from "../loaders";

export type ButtonVariant = "primary" | "outlined" | "caution" | "ghost";

export interface ButtonProps extends React.PropsWithChildren<unknown> {
    /**
     * Additional className for the button
     */
    className?: string;
    /**
     * If button is disabled
     */
    disabled?: boolean;
    /**
     * Icon displayed with the button
     */
    icon?: Icons;
    /**
     * Additional className for the icon
     */
    iconClassName?: string;
    /**
     * Position of the icon
     */
    iconPosition?: IconPosition;
    /**
     * Additional props for icon
     */
    iconProps?: IconWrapperProps;
    /**
     * Size of the icon
     */
    iconSize?: IconSize;
    /**
     * If is loading
     */
    isLoading?: boolean;
    /**
     * Label of the button
     */
    label?: string;
    /**
     * Click handler
     */
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    /**
     * If is submit button
     */
    submit?: boolean;
    /**
     * Variant of button
     */
    variant?: ButtonVariant | "tab";
    /**
     * fullWidth
     */
    fullWidth?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        children,
        className,
        disabled: disabledProp = false,
        icon,
        iconClassName: iconClassNameProp,
        iconPosition = "left",
        iconProps,
        iconSize = "s",
        isLoading = false,
        label,
        onClick,
        submit = false,
        variant = "primary",
        fullWidth = false,
        ...otherProps
    },
    ref
) {
    const disabled = disabledProp || isLoading;

    const buttonClassName = clsx(
        {
            "ds-theme--color": !disabled && ["primary", "caution"].includes(variant),
            "ds-o-actionable": ["primary", "caution", "ghost", "tab", "outlined"].includes(variant),
        },
        "ds-c-button",
        {
            "ds-c-button--primary": variant === "primary",
            "ds-c-button--outlined": variant === "outlined",
            "ds-c-button--caution": variant === "caution",
            "ds-c-button--ghost": variant === "ghost",
            "ds-c-button--full-width": fullWidth,
            "ds-c-button--width-max-content": !fullWidth,
        },
        "ds-u-typography--button",
        className
    );
    const iconClassName = clsx("ds-c-button__icon", iconClassNameProp);

    const wrappedChildren = wrapLabel(label ?? children);

    const handleOnClick = wrapPrevent(onClick);
    const handleKeyUp = wrapKeyUp(handleOnClick);

    return (
        <button
            className={buttonClassName}
            disabled={disabled === true}
            onClick={handleOnClick}
            onKeyUp={handleKeyUp}
            ref={ref}
            type={submit ? "submit" : "button"}
            {...otherProps}
        >
            <IconWrapper
                {...iconProps}
                className={iconClassName}
                icon={isLoading ? <LoaderIcon /> : icon}
                position={iconPosition}
                size={iconSize}
                condensed
            >
                {wrappedChildren}
            </IconWrapper>
        </button>
    );
});
